import React, { useEffect } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSidebarContext } from '../Sidebar';
import { useSelfSetupTour } from '../../hooks/useSelfSetupTour';
import { SetupCoachmark } from './SetupCoachmark';

type SelfSetupNavTourEffectsProps = {
  onRevealSidebar: () => void;
};

/** Expand/open the sidebar while a welcome nav tip is active. */
export function SelfSetupNavTourEffects({
  onRevealSidebar,
}: SelfSetupNavTourEffectsProps) {
  const { isStep, tourPhase } = useSelfSetupTour();
  const showNavTip =
    tourPhase === 'nav' &&
    (isStep('dashboard') || isStep('esgReports'));

  useEffect(() => {
    if (showNavTip) onRevealSidebar();
  }, [showNavTip, onRevealSidebar]);

  return null;
}

type SelfSetupAwareNavItemProps = {
  path: string;
  children: React.ReactNode;
};

/**
 * First tip of each welcome step: highlight Dashboard / ESG Reports in the sidebar.
 */
export function SelfSetupAwareNavItem({
  path,
  children,
}: SelfSetupAwareNavItemProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { drawerVariant } = useSidebarContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isStep, tourPhase, setTourPhase, dismissTour } = useSelfSetupTour();

  const inVisibleDrawer =
    (isMobile && drawerVariant === 'temporary') ||
    (!isMobile && drawerVariant === 'permanent');

  const showDashboardNav =
    inVisibleDrawer &&
    path === '/' &&
    isStep('dashboard') &&
    tourPhase === 'nav';
  const showReportsNav =
    inVisibleDrawer &&
    path === '/esg-reports' &&
    isStep('esgReports') &&
    tourPhase === 'nav';
  const showTip = showDashboardNav || showReportsNav;

  const advance = () => {
    if (showDashboardNav) {
      navigate('/');
      setTourPhase('monthlyTab');
      return;
    }
    if (showReportsNav) {
      navigate('/esg-reports');
      setTourPhase('period');
    }
  };

  if (!showTip) return <>{children}</>;

  return (
    <SetupCoachmark
      open
      compact
      fullWidth
      placement="right"
      title={
        showDashboardNav
          ? t('selfSetup.tour.dashboardNavTitle')
          : t('selfSetup.tour.esgReportsNavTitle')
      }
      subtitle={
        showDashboardNav
          ? t('selfSetup.tour.dashboardNavSubtitle')
          : t('selfSetup.tour.esgReportsNavSubtitle')
      }
      onDismiss={() => dismissTour('nav')}
    >
      <Box
        id={
          showDashboardNav
            ? 'self-setup-nav-dashboard'
            : 'self-setup-nav-esg-reports'
        }
        onClickCapture={(event) => {
          event.preventDefault();
          event.stopPropagation();
          advance();
        }}
        sx={{
          borderRadius: 1.5,
          bgcolor: theme.palette.background.paper,
        }}
      >
        {children}
      </Box>
    </SetupCoachmark>
  );
}
